import { useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Link2, Loader2, Search, Plus, Package, Pencil, X, AlertTriangle, ArrowLeftRight,
  ChevronDown, ChevronRight, Eye, Map as MapIcon, Type, MapPin, FileText, ExternalLink, Home, PersonStanding,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn, mapUrl, mapPinUrl, addressPoint } from '@/lib/utils';
import { StreetViewIconButton, StreetViewPanel, useStreetView } from '@/components/custom/StreetView';
import { apiFetch } from '@/lib/api-client';
import { formatDeliveryStatus, isBlockedDeliveryCode } from '@/lib/deliveryStatus';
import { AddressReferenceView } from '@/components/tickets/AddressReferenceView';
import { useServiceFileFixes } from '@/components/dmp/ServiceFileFixes';
import { AddressStatusFix } from '@/components/dmp/AddressStatusFix';
import { NeighboursSection } from '@/components/dmp/NeighboursSection';
import { ConsoleMappingBlock } from '@/components/dmp/ConsoleMappingBlock';
import { AddressCommentsBlock } from '@/components/dmp/AddressCommentsBlock';
import { AttentionDetails } from '@/components/tickets/AttentionPoints';
import { computeAttentionPoints } from '@uniticket/shared';
import type {
  DmpLookupResponse, DmpStreetSearchItem, BuildFileHit, EnrichmentSsvTsa,
  DmpConsoleMappingResponse, DmpConsoleMappingRow,
} from '@uniticket/shared';

// The DMP console itself — the search fields and everything they display. Extracted from the drawer
// (2026-08-07) so the SAME console can be shown in two places: the global drawer every internal user
// opens from a ticket, and the standalone page a BUILD account gets as its only screen. One
// implementation, so the two can never drift apart.
//
// It owns its own state and is strictly READ-ONLY. The one write the drawer offers — « lier ce jvid au
// ticket » — is NOT here: the panel merely renders the button and hands the loaded address back through
// `onLink`, leaving the confirm + POST to the caller. A page with no ticket simply passes nothing, and
// there is no write path at all.

/** Optional address pre-fill — used when the caller has an ADDRESS but no jvid (no-address ticket). */
export type DmpLookupPrefill = { street?: string | null; number?: string | null; ext?: string | null; postal?: string | null };

/** Ticket context: when the console is opened FROM a ticket, the record view can offer "link this jvid
 *  to the ticket" — only meaningful while the ticket has no jvid (linkable). */
export type DmpLookupTicketCtx = { ticketId: string; ticketNumber: string; linkable: boolean };

/** What the caller asks the panel to show. `nonce` is what makes a REPEATED request (same jvid twice)
 *  take effect; without it the panel would ignore the second open. A request that carries neither a
 *  jvid nor a usable street leaves the panel as it was — reopening the drawer keeps the previous
 *  result on screen, which is the behaviour the reviewers rely on. */
export type DmpConsoleRequest = { jvid?: number | null; prefill?: DmpLookupPrefill; nonce: number };

/** One-line address: street + house number + the extension when it adds information. DMP convention:
 *  the extension is the FULL alphanumeric number, so it is THE distinguishing suffix (user case
 *  2026-08-01: Gilly 171 vs 171A rendered as two identical « Chaussée de Gilly 171 » search rows). */
export const addressLine = (street: string | null, houseNumber: string | null, ext: string | null) =>
  [street, houseNumber, ext && ext !== houseNumber ? ext : null].filter(Boolean).join(' ');

/** WHY the jvid is blacklisted — column `QWC` of the blacklist export (« JVID on an archived address »).
 *  Looked up by NAME, case-insensitively, and optional by design: each file carries its own header set
 *  and it changes from one export to the next, so a renamed column costs the reason, never the pill. */
const BLACKLIST_REASON_COLUMN = 'QWC';
function blacklistReason(hit: BuildFileHit): string | null {
  if (!hit.found) return null;
  const cell = (hit.cells ?? []).find((c) => c.column.trim().toUpperCase() === BLACKLIST_REASON_COLUMN);
  return cell?.value.trim() || null;
}

/**
 * The verdict line (user ask 2026-08-11): commercialised? meant to be? in the Build PSTs? — answered
 * ABOVE the fiche instead of below 105 columns of it. Lives in the panel rather than in the page header
 * so the drawer opened from a ticket gets it too: knowing whether an address is commercialised matters
 * most while validating one.
 *
 * A pill only appears when its SOURCE is loaded. With no service file uploaded, a grey "absent" pill
 * would read as an answer when the truth is "we have nothing to answer with" — so it is simply absent.
 *
 * Each pill is a BUTTON (user ask 2026-08-11): the verdict is what you need at a glance, the 39-to-45
 * columns behind it only when you ask for them. Clicking opens the detail in a modal instead of pinning
 * three tables under every lookup.
 */
function VerdictPills({ data, onOpen }: { data: DmpLookupResponse; onOpen: (key: string) => void }) {
  const { t } = useTranslation(['tickets']);

  // `bad` inverts the reading: found in the blacklist is NOT good news, and a green pill would say the
  // exact opposite of what it means. Absent stays neutral grey in both cases.
  //
  // The bad case is SOLID RED, not a tint (user ask 2026-08-12: « je veux des couleurs plus flash »).
  // The pale amber it used to wear read like just another quiet chip next to the green ones — the whole
  // point of the line is to be scanned in a second, and the one entry that means "stop" has to win that
  // second. The warning icon comes with it so the colour is never the only signal, and the REASON is
  // spelled out inline: « pourquoi » was a click away in the modal, which is one click too many for a
  // verdict line.
  const pill = (
    key: string, label: string, found: boolean, title?: string, bad = false, reason?: string | null,
  ) => (
    <button
      key={key}
      type="button"
      title={reason ? `${title ?? label} — ${reason}` : title}
      onClick={() => onOpen(key)}
      aria-label={`${label}${found && bad && reason ? ` — ${reason}` : ''} — ${t('tickets:dmpLookup.buildFile.openDetail')}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold transition-shadow',
        'hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-rose/40',
        !found
          ? 'bg-slate-100 text-slate-500'
          : bad
            ? 'bg-red-600 uppercase tracking-wide text-white shadow-sm hover:shadow-md'
            : 'bg-emerald-50 text-emerald-700',
      )}
    >
      {found && bad ? (
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      ) : (
        <span className={cn('h-1.5 w-1.5 rounded-full', found ? 'bg-emerald-500' : 'bg-slate-300')} />
      )}
      {label}
      {found && bad && reason && (
        // Truncated, never wrapped: the line holds four pills and a long motif would push the others
        // off. The full text stays in the tooltip and in the modal behind the pill.
        <span className="max-w-[16rem] truncate font-semibold normal-case text-red-100">· {reason}</span>
      )}
    </button>
  );

  // SSV / TSA (2026-09-14) — UNE seule pastille pour les trois documents (décision user du 14-09, qui
  // remplace les deux pastilles du premier jet) : VERTE dès qu'UN SEUL des trois existe. Le détail de
  // qui est là et qui manque vit dans la modale, pas dans la couleur.
  //
  // Le verdict est pris sur TOUS les items appariés : la clé de jointure est une chaîne d'adresse, donc
  // un jvid peut légitimement en matcher plusieurs.
  //
  // `data.ssvTsa == null` (lecture en échec / DMP coupé) masque la pastille ; `[]` la montre grise —
  // cette distinction est tout l'intérêt, voir le commentaire de DmpLookupResponse.ssvTsa.
  const docs = data.ssvTsa ?? [];
  const anyDoc = docs.some((d) => d.ssvDocLink || d.tsaDocForSignature || d.tsaDocSigned);
  const tsaSignedDate = docs.find((d) => d.tsaDocSigned)?.tsaSignedDate ?? null;

  const pills = [
    ...(data.buildFiles ?? []).map((h) =>
      pill(
        h.kind,
        t(`tickets:dmpLookup.buildFile.${h.kind}`),
        h.found,
        t(`tickets:dmpLookup.buildFile.${h.kind}Hint`),
        h.kind === 'BLACKLIST',
        h.kind === 'BLACKLIST' ? blacklistReason(h) : null,
      )),
    ...(data.pstIndexReady
      ? [pill('PST', t('tickets:dmpLookup.pstPill'), !!data.pstFile, data.pstFile?.split('/').pop() ?? undefined)]
      : []),
    ...(data.ssvTsa
      ? [
          pill(
            'SSVTSA',
            t('tickets:dmpLookup.ssvTsa.pill'),
            anyDoc,
            // La date de signature est l'information la plus utile quand elle existe ; sinon on rappelle
            // simplement ce que la pastille couvre.
            tsaSignedDate
              ? t('tickets:dmpLookup.ssvTsa.tsaSignedOn', { date: tsaSignedDate })
              : t('tickets:dmpLookup.ssvTsa.hint'),
          ),
        ]
      : []),
  ];
  if (pills.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-3">
      {pills}
      <span className="text-[10px] text-slate-400">{t('tickets:dmpLookup.buildFile.openDetail')}</span>
    </div>
  );
}

/** Une entrée de menu qui agit (bouton) ou qui ouvre un lien externe (ancre) — même allure. Un bouton peut être
 *  grisé, avec son pourquoi au survol (Street View sans coordonnées). */
function MenuRow({ icon: Icon, label, onClick, href, disabled, title }: {
  icon: typeof Eye; label: string; onClick?: () => void; href?: string; disabled?: boolean; title?: string;
}) {
  const cls = 'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-brand-rose/[0.06] hover:text-slate-900 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-slate-400';
  const inner = <><Icon className={cn('h-4 w-4 shrink-0', disabled ? 'text-slate-300' : 'text-brand-rose')} />{label}</>;
  return href
    ? <a role="menuitem" href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} title={title} className={cls}>{inner}</a>
    : <button type="button" role="menuitem" onClick={onClick} disabled={disabled} title={title} className={cls}>{inner}</button>;
}

/** Un intitulé qui déplie ses entrées au lieu de les étaler. Le chevron pointe à DROITE tant que c'est
 *  fermé, vers le BAS une fois ouvert — la convention du dépliement, pas celle d'un sous-menu qui
 *  s'ouvrirait sur le côté (il déborderait de l'écran sur un menu déjà collé au bord). */
function MenuGroup({ icon: Icon, label, open, onToggle, children }: {
  icon: typeof Eye; label: string; open: boolean; onToggle: () => void; children: ReactNode;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-brand-rose/[0.06] hover:text-slate-900"
      >
        <Icon className="h-4 w-4 shrink-0 text-brand-rose" />
        <span className="flex-1">{label}</span>
        <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform', open && 'rotate-90')} />
      </button>
      {open && <div className="ml-3 border-l border-slate-100 pl-1">{children}</div>}
    </div>
  );
}

/**
 * Toutes les actions de la fiche dans UN menu (demande user 2026-09-02 : « il commence à y avoir
 * beaucoup de boutons »). Quatre boutons alignés devenaient une barre d'outils qu'il fallait lire en
 * entier pour trouver le geste voulu ; ils tenaient encore à quatre, plus du tout à six.
 *
 * VOIR d'abord, AGIR ensuite, séparés par un trait : on ouvre la carte pour se décider, et les écritures
 * ne doivent pas se trouver sous le doigt qui visait Street View. Les deux liens carte sont offerts à
 * TOUS — ce ne sont que des liens — là où les écritures n'apparaissent que si la page en a passé le
 * rappel, c'est-à-dire aux admins.
 *
 * Les liens reprennent tels quels les helpers et la hiérarchie du panneau Release (2026-08-28) :
 *   • les COORDONNÉES du DMP d'abord — le point calculé depuis x/y (`addressPoint` ; depuis le 2026-10-16,
 *     parce que le lat/lon stocké des adresses créées par l'app est à ~836 m), la carte épinglée sur ce point,
 *     celle qu'on mesure jusqu'à la rue ;
 *   • Street View DANS l'app (2026-10-16) : le bonhomme à côté d'« Actions » et l'entrée du menu ouvrent le
 *     même panneau. Sans coordonnées les deux se grisent : le viewer ne sait pas partir d'un texte ;
 *   • le TEXTE de l'adresse en repli AUTOMATIQUE pour la carte quand les coordonnées manquent ;
 *   • et ce même repli offert À LA MAIN quand elles existent, parce qu'un point qui tombe à côté ne se
 *     détecte PAS depuis le code : une fois l'URL rendue au navigateur on ne sait plus ce que Google en a
 *     fait (aucun panorama à cet endroit, ou un x/y lui-même faux).
 */
function AddressActionsMenu({
  data, ticket, linkBusy, onLink, onCreateAddress, onAddBox, onEditAddress, onSwapBusNumber, onNeighbours,
}: {
  data: DmpLookupResponse;
  // On recopie la largeur des props du panneau : il porte `ticket` nullable et `linkBusy` optionnel.
  ticket?: DmpLookupTicketCtx | null;
  linkBusy?: boolean;
  onLink?: (d: DmpLookupResponse) => void;
  onCreateAddress?: (d: DmpLookupResponse) => void;
  onAddBox?: (d: DmpLookupResponse) => void;
  onEditAddress?: (d: DmpLookupResponse) => void;
  onSwapBusNumber?: (d: DmpLookupResponse) => void;
  /** « Voisin » (25-09) — une façon de REGARDER, donc rangée avec les cartes, avant le trait des écritures. */
  onNeighbours?: () => void;
}) {
  const { t } = useTranslation(['tickets']);
  const [open, setOpen] = useState(false);
  // Replié à chaque ouverture du menu : le geste courant est d'aller aux actions d'écriture, pas de
  // rouvrir la carte qu'on vient de consulter.
  const [mapOpen, setMapOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) { setOpen(false); setMapOpen(false); }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const r = data.reference;
  const point = addressPoint(r);
  const sv = useStreetView(point);
  const fromText = mapUrl([
    r.streetName,
    (r.houseNumberExtension || r.houseNumber || '').trim() || null,
    r.postalCode,
    r.city,
  ]);
  const svLabel = [
    addressLine(r.streetName, r.houseNumber, r.houseNumberExtension) + (r.busNumber?.trim() ? ` / ${r.busNumber}` : ''),
    [r.postalCode, r.city || r.municipality].filter(Boolean).join(' '),
  ].filter(Boolean).join(' · ');

  const act = (fn?: (d: DmpLookupResponse) => void) => () => { setOpen(false); fn?.(data); };
  const writes = (ticket?.linkable && onLink) || onCreateAddress || onAddBox || onEditAddress || onSwapBusNumber;

  return (
    <div className="flex items-center gap-2">
      <div className="relative" ref={wrapRef}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => { setOpen((o) => !o); setMapOpen(false); }}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {linkBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
          {t('tickets:dmpLookup.actionsBtn')}
          <ChevronDown className={cn('ml-1 h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
        </Button>

        {open && (
          // z-40 : au-dessus des cartes de la fiche, sous les modales et le tiroir (z-50).
          <div role="menu" className="absolute left-0 z-40 mt-1 w-72 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
            {/* Les trois façons de regarder tiennent sous UN intitulé (demande user 2026-09-02) : étalées,
                elles occupaient la moitié du menu et l'une d'elles passait à la ligne, pour un geste qu'on
                ne fait pas à chaque ouverture. */}
            <MenuGroup icon={MapIcon} label={t('tickets:dmpLookup.mapGroup')} open={mapOpen} onToggle={() => setMapOpen((o) => !o)}>
              <MenuRow
                icon={PersonStanding}
                label={t('tickets:dmpLookup.streetView')}
                onClick={() => { setOpen(false); setMapOpen(false); sv.show(); }}
                disabled={!point}
                title={point ? undefined : t('tickets:streetViewPanel.noCoords')}
              />
              <MenuRow
                icon={MapPin}
                label={t('tickets:dmpLookup.openMap')}
                href={point ? mapPinUrl(point.lat, point.lon) : fromText}
                onClick={() => setOpen(false)}
              />
              {point ? (
                <MenuRow icon={Type} label={t('tickets:dmpLookup.mapFromText')} href={fromText} onClick={() => setOpen(false)} />
              ) : (
                <p className="px-3 py-1.5 text-[10px] text-slate-400">{t('tickets:dmpLookup.mapNoCoords')}</p>
              )}
            </MenuGroup>
            {onNeighbours && (
              <MenuRow icon={Home} label={t('tickets:dmpLookup.neighboursBtn')} onClick={() => { setOpen(false); onNeighbours(); }} />
            )}

            {writes && <div className="my-1 h-px bg-slate-100" />}

            {ticket?.linkable && onLink && (
              <MenuRow icon={Link2} label={t('tickets:dmpLookup.linkBtn', { ticket: ticket.ticketNumber })} onClick={act(onLink)} />
            )}
            {onCreateAddress && <MenuRow icon={Plus} label={t('tickets:dmpLookup.createAddressBtn')} onClick={act(onCreateAddress)} />}
            {onAddBox && <MenuRow icon={Package} label={t('tickets:dmpLookup.addBoxBtn')} onClick={act(onAddBox)} />}
            {onEditAddress && <MenuRow icon={Pencil} label={t('tickets:dmpLookup.editBtn')} onClick={act(onEditAddress)} />}
            {onSwapBusNumber && <MenuRow icon={ArrowLeftRight} label={t('tickets:dmpLookup.swapBtn')} onClick={act(onSwapBusNumber)} />}
          </div>
        )}
      </div>
      {/* Le bonhomme (2026-10-16) : Street View à un clic, sans passer par le menu. */}
      <StreetViewIconButton point={point} active={sv.open} onClick={sv.toggle} className="h-8 w-8 rounded-md" />
      {sv.open && point && <StreetViewPanel point={point} label={svLabel} onClose={sv.close} />}
    </div>
  );
}

/** One SharePoint document. A MISSING document is still rendered, as a dead grey line: « pas de TSA
 *  signé » is the answer the reader came for, and a row that simply vanishes leaves them wondering
 *  whether the app looked at all. The link opens in a new tab — the console is a working screen and
 *  losing it to a PDF would cost the whole lookup.
 *
 *  `flex w-fit` and not `inline-flex` (retour user 14-09) : the three documents are a LIST, read down
 *  the left edge, so each takes its own line whether it exists or not. An inline anchor let two present
 *  links share a line while the absent one sat below — the same three rows rendering in two different
 *  shapes depending on the data. `w-fit` keeps the clickable area on the text instead of stretching it
 *  across the card, which block-level alone would have done. */
function DocLink({ href, label }: { href: string | null; label: string }) {
  const { t } = useTranslation(['tickets']);
  if (!href) {
    return (
      <p className="flex w-fit items-center gap-1.5 text-xs text-slate-400">
        <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>{label}</span>
        <span className="text-slate-300">·</span>
        <span>{t('tickets:dmpLookup.ssvTsa.missing')}</span>
      </p>
    );
  }
  return (
    // rel="noopener noreferrer" is not optional here: the href comes from a cell on a board we do not
    // own, so the opened page must never reach back into ours through window.opener.
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex w-fit items-center gap-1.5 text-xs font-semibold text-brand-rose hover:underline"
    >
      <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{label}</span>
      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
    </a>
  );
}

/** The detail behind the single SSV / TSA pill — the three documents plus the Monday statuses.
 *
 *  The pill is green as soon as ONE of the three exists (user decision 2026-09-14), so the colour no
 *  longer says WHICH one: this card carries that answer, and it is the only place that does. Hence the
 *  three lines are always rendered, missing ones included — « le TSA n'est pas signé » is read here or
 *  nowhere.
 *
 *  Several items can match one address (the join key is an address string), so this lists them all and
 *  names each by its `task` — the reader needs to see WHICH row carries the signature. */
export function SsvTsaCard({ docs }: { docs: EnrichmentSsvTsa[] }) {
  const { t } = useTranslation(['tickets']);

  if (docs.length === 0) {
    return (
      <div className="p-4">
        <p className="text-sm text-slate-700">{t('tickets:dmpLookup.ssvTsa.none')}</p>
        <p className="mt-2 text-[11px] text-slate-400">{t('tickets:dmpLookup.ssvTsa.noneHint')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4">
      {docs.map((d, i) => (
        <div key={d.task ?? i} className="rounded-lg border border-slate-200 p-3">
          <p className="break-all font-mono text-[11px] text-slate-500">{d.task ?? '∅'}</p>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
            <dt className="text-slate-500">{t('tickets:dmpLookup.ssvTsa.statusSsv')}</dt>
            <dd className="text-slate-700">{d.ssvStatus ?? '∅'}</dd>
            <dt className="text-slate-500">{t('tickets:dmpLookup.ssvTsa.statusTsa')}</dt>
            <dd className="text-slate-700">{d.tsaStatus ?? '∅'}</dd>
            {d.tsaSignedDate && (
              <>
                <dt className="text-slate-500">{t('tickets:dmpLookup.ssvTsa.signedOn')}</dt>
                <dd className="font-semibold text-emerald-700">{d.tsaSignedDate}</dd>
              </>
            )}
          </dl>
          <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-2.5">
            <DocLink href={d.ssvDocLink} label={t('tickets:dmpLookup.ssvTsa.ssvDoc')} />
            <DocLink href={d.tsaDocForSignature} label={t('tickets:dmpLookup.ssvTsa.tsaDocForSignature')} />
            <DocLink href={d.tsaDocSigned} label={t('tickets:dmpLookup.ssvTsa.tsaDocSigned')} />
          </div>
        </div>
      ))}
      <p className="text-[11px] text-slate-400">{t('tickets:dmpLookup.ssvTsa.source')}</p>
    </div>
  );
}

/** The detail behind a pill. Same dependency-free shell as ConfirmDialog (no Dialog primitive in this
 *  project): blurred overlay, Esc and backdrop close, dialog semantics.
 *
 *  RENDERED THROUGH A PORTAL, and that is not decoration. This panel lives inside a GlassCard, whose
 *  `backdrop-filter: blur(16px)` makes it the containing block of its `position: fixed` descendants —
 *  so `inset-0` covered the CARD instead of the viewport and the dialog centred itself far below the
 *  fold: the user saw a smoked screen and had to scroll to find it (reported 2026-08-11). Portalling to
 *  document.body takes the overlay out of that subtree, where `fixed` means the viewport again. */
export function DetailModal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const { t } = useTranslation(['tickets']);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    // `pointer-events-auto` et `z-[60]` ne sont PAS décoratifs — ils règlent un défaut constaté le
    // 14-09 : dans le TIROIR (ticket → Manage → console DMP), la modale s'affichait mais aucun lien
    // n'était cliquable.
    //
    // Le tiroir est un Radix Sheet ouvert en mode modal : Radix pose alors `pointer-events: none` sur
    // <body> et ne rend `auto` qu'à son propre contenu. Cette modale étant portalisée sur document.body,
    // elle tombe HORS du SheetContent et hérite donc du `none` — visible, inerte. Et les deux couches
    // partageaient `z-50`, ce qui laissait l'empilement dépendre de l'ordre d'insertion des portails.
    //
    // Le défaut touchait déjà les modales Blacklist et PST ; il passait inaperçu parce qu'elles n'ont
    // rien à cliquer et qu'Échap les ferme (l'écouteur est sur window, pas sur la couche).
    <div
      className="pointer-events-auto fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px] animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-xl"
      >
        {children}
        <div className="border-t border-slate-100 px-4 py-3 text-right">
          <Button size="sm" variant="outline" onClick={onClose}>
            <X className="mr-1.5 h-4 w-4" />
            {t('tickets:dmpLookup.buildFile.close')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** One Build reference file's answer for the looked-up jvid. "Absent" is a RESULT, not an empty state:
 *  being in the deployment list without being in the service file means it was meant to be
 *  commercialised and is not (yet), which is exactly what the reviewer is here to see. Empty columns are
 *  folded away by default — these exports carry 39 and 45 of them and most are blank on a given row. */
export function BuildFileCard({ hit }: { hit: BuildFileHit }) {
  const { t } = useTranslation(['tickets']);
  const [showEmpty, setShowEmpty] = useState(false);
  const filled = hit.cells?.filter((c) => c.value !== '') ?? [];
  const empty = hit.cells?.filter((c) => c.value === '') ?? [];
  const shown = showEmpty ? [...filled, ...empty] : filled;
  const stamp = (iso: string) => new Date(iso).toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' });
  const when = stamp(hit.uploadedAt);
  // The date the file was MADE, not loaded: an absence only means something against it (R1, 2026-09-25).
  const made = hit.fileDate ? stamp(hit.fileDate) : null;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <div className="flex items-start justify-between gap-2 bg-slate-50 px-3 py-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            {t(`tickets:dmpLookup.buildFile.${hit.kind}`)}
          </p>
          <p className="text-[10px] text-slate-400">{t(`tickets:dmpLookup.buildFile.${hit.kind}Hint`)}</p>
        </div>
        <span
          className={cn(
            'whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold',
            !hit.found
              ? 'bg-slate-100 text-slate-500'
              // Same solid red as the pill that opened this modal — a verdict must not soften on the
              // way to its own detail.
              : hit.kind === 'BLACKLIST'
                ? 'bg-red-600 text-white'
                : 'bg-emerald-50 text-emerald-700',
          )}
        >
          {hit.found ? t('tickets:dmpLookup.buildFile.present') : t('tickets:dmpLookup.buildFile.absent')}
        </span>
      </div>

      {hit.found ? (
        <>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-[11px]">
              <tbody>
                {shown.map((c) => (
                  <tr key={c.column} className="border-t border-slate-100 first:border-t-0">
                    <td className="whitespace-nowrap px-3 py-1 font-mono font-medium text-slate-600">{c.column}</td>
                    <td className={cn('break-all px-3 py-1 font-mono', c.value ? 'text-slate-800' : 'text-slate-300')}>
                      {c.value || '∅'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {empty.length > 0 && (
            <button
              type="button"
              onClick={() => setShowEmpty((v) => !v)}
              className="w-full border-t border-slate-100 px-3 py-1.5 text-left text-[10px] font-medium text-slate-500 hover:text-brand-rose"
            >
              {showEmpty
                ? t('tickets:dmpLookup.buildFile.hideEmpty')
                : t('tickets:dmpLookup.buildFile.showEmpty', { n: empty.length })}
            </button>
          )}
        </>
      ) : (
        <p className="px-3 py-3 text-[11px] text-slate-500">{t('tickets:dmpLookup.buildFile.absentBody')}</p>
      )}

      <p className="border-t border-slate-100 px-3 py-1.5 text-[10px] text-slate-400">
        {made
          ? t('tickets:dmpLookup.buildFile.sourceDated', { file: hit.filename, date: made, rows: hit.rowCount, when })
          : t('tickets:dmpLookup.buildFile.source', { file: hit.filename, rows: hit.rowCount, when })}
      </p>
    </div>
  );
}

export function DmpConsolePanel({
  request,
  ticket,
  onLink,
  linkBusy,
  onCreateAddress,
  onAddBox,
  onEditAddress,
  onSwapBusNumber,
  onForceBlacklist,
  onEditColumn,
  onEditMapping,
  showNeighbours,
}: {
  request?: DmpConsoleRequest | null;
  ticket?: DmpLookupTicketCtx | null;
  onLink?: (data: DmpLookupResponse) => void;
  linkBusy?: boolean;
  /** ADMIN-only creations offered by the console PAGE (user ask 2026-08-11), anchored on the loaded
   *  address instead of a ticket. Same contract as onLink: the panel renders the button and hands the
   *  loaded address back — the confirm, the form and the POST all live in the caller. Callers that pass
   *  neither (the ticket drawer, and the page for a non-admin) get exactly the read-only console. */
  onCreateAddress?: (data: DmpLookupResponse) => void;
  onAddBox?: (data: DmpLookupResponse) => void;
  /** ADMIN-only correction of the loaded address's identity (user ask 2026-08-28) — same contract
   *  again: the panel offers the button, the caller owns the form and the PATCH. */
  onEditAddress?: (data: DmpLookupResponse) => void;
  /** ADMIN-only exchange of two addresses' box numbers (user ask 2026-09-01). The loaded address is
   *  one side of the pair; the caller's modal asks for the other jvid. */
  onSwapBusNumber?: (data: DmpLookupResponse) => void;
  /** ADMIN-only « Forcer » (user ask 2026-09-17): from the red blacklist pill, write a line with NO address
   *  change into the blacklist feedback file. Same contract again — the page owns the preview and the POST. */
  onForceBlacklist?: (data: DmpLookupResponse) => void;
  /** ADMIN-only raw edit of ANY column (user ask 2026-09-17: « on doit pouvoir tout modifier »): a pencil on
   *  each line of the « all DMP columns » block. The page owns the editor and the PATCH. */
  onEditColumn?: (data: DmpLookupResponse, column: string) => void;
  /** ADMIN-only (N39, 08-10) : créer ou modifier ce qu'un opérateur déclare pour le JVID chargé, hors ticket. Le bloc
   *  « Table de mapping » se LIT par tous ; ses boutons n'apparaissent que si la page passe ceci. `row` = `null`
   *  → créer. Même contrat que les autres : la page possède le formulaire et le PUT. */
  onEditMapping?: (data: DmpLookupResponse, mapping: DmpConsoleMappingResponse, row: DmpConsoleMappingRow | null) => void;
  /** « Voisin » (25-09) : les adresses du même côté de la rue, ouvertes depuis Actions. La PAGE seule le passe —
   *  décision du user : pas dans le tiroir ouvert depuis un ticket. */
  showNeighbours?: boolean;
}) {
  const { t } = useTranslation(['tickets']);
  const [input, setInput] = useState('');
  // La vue Voisins, une fois ouverte, SUIT l'adresse consultée (ouvrir une carte recentre la série) jusqu'à la croix.
  const [neighboursOpen, setNeighboursOpen] = useState(false);
  const [data, setData] = useState<DmpLookupResponse | null>(null);
  // L'instant du dernier chargement réussi : ce qui se lit À PART de la fiche (la table de mapping) se relit avec.
  const [loadedAt, setLoadedAt] = useState(0);
  // Which pill's detail is open ('PST' or a BuildFileKind) — nothing is rendered until one is clicked.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawFilter, setRawFilter] = useState('');

  // N5 (demande 2026-09-03, spécifiée le 2026-09-11) — le filtre du bloc « toutes les colonnes DMP ».
  // Trois règles tranchées qu'il ne faut pas « simplifier » plus tard :
  //  · on MASQUE ce qui ne correspond pas, on ne surligne pas ;
  //  · `rawTotal` reste ce qu'affiche le titre — ne JAMAIS le brancher sur la liste filtrée ;
  //  · le prédicat ne regarde QUE la clé : une colonne VIDE dont le nom correspond reste affichée avec
  //    son ∅, parce que « elle existe et elle est vide » est justement ce que ce bloc sert à dire.
  const rawTotal = data?.raw ? Object.keys(data.raw).length : 0;
  const rawQuery = rawFilter.trim().toLowerCase();
  const rawRows = data?.raw
    ? Object.entries(data.raw).filter(([col]) => rawQuery === '' || col.toLowerCase().includes(rawQuery))
    : [];

  const load = useCallback(async (jvid: number) => {
    setLoading(true);
    setError(null);
    setData(null);
    // Le filtre ne survit PAS à un changement d'adresse : le bloc promet « rien n'est caché », et un
    // filtre rémanent le contredirait en silence (3 lignes affichées sous un titre qui annonce 105).
    setRawFilter('');
    // Le champ JVID n'est pas réécrit ici : c'est `goTo` qui décide (recherche par adresse en cours ou non).
    try {
      const res = await apiFetch<{ data: DmpLookupResponse }>(`/dmp/address/${jvid}`);
      setData(res.data);
      setLoadedAt(Date.now());
    } catch (err) {
      setError((err as Error)?.message || t('tickets:dmpLookup.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Les boutons de réparation du verdict service file — CONSOLE UNIQUEMENT (contrainte user 31-08).
  // `fixModal` est l'aperçu du fake ODF : il est rendu à côté du verdict, pas dans la ligne du bouton.
  const { renderFix, fixModal } = useServiceFileFixes(
    data?.reference.jvid ?? null,
    data?.attributes ?? null,
    () => { if (data) void load(data.reference.jvid); },
    data ? addressLine(data.reference.streetName, data.reference.houseNumber, data.reference.houseNumberExtension) : null,
  );

  // Street search (user ask 2026-07-30): free-text street + optional number — results are clickable
  // and open the jvid lookup above. Independent of the jvid flow (its own loading/error).
  const [sStreet, setSStreet] = useState('');
  const [sNumber, setSNumber] = useState('');
  const [sExt, setSExt] = useState('');
  const [sPostal, setSPostal] = useState('');
  const [sResults, setSResults] = useState<DmpStreetSearchItem[] | null>(null);
  const [sLoading, setSLoading] = useState(false);
  const [sError, setSError] = useState<string | null>(null);

  // Les deux recherches s'excluent (demande du user, 17-09) : lancer l'une vide l'autre, sinon les deux blocs restent
  // remplis et semblent actifs en même temps. On vide au LANCEMENT, jamais à la saisie — recopier dans le champ JVID
  // un numéro lu dans la liste de résultats doit rester possible.
  const clearStreetSearch = () => {
    setSStreet('');
    setSNumber('');
    setSExt('');
    setSPostal('');
    setSResults(null);
    setSError(null);
  };
  const clearJvidSearch = () => {
    setInput('');
    setError(null);
    // La fiche chargée disparaît aussi : elle ne correspondrait plus à rien de ce qui est saisi.
    setData(null);
    setOpenKey(null);
  };
  // Aller sur une adresse dont l'utilisateur n'a pas tapé le JVID (résultat de recherche, logement du bâtiment,
  // ouverture depuis un ticket, rechargement après une modification) : le champ JVID suit, SAUF pendant une
  // recherche par adresse, qui doit rester la seule active.
  const goTo = (jvid: number) => {
    if (!sResults) setInput(String(jvid));
    void load(jvid);
  };

  const searchStreetWith = useCallback(async (street: string, number: string, ext: string, postal: string) => {
    if (street.trim().length < 3) return;
    setSLoading(true);
    setSError(null);
    try {
      const params = new URLSearchParams({ street: street.trim() });
      if (number.trim()) params.set('number', number.trim());
      if (ext.trim()) params.set('ext', ext.trim());
      if (postal.trim()) params.set('postalCode', postal.trim());
      const res = await apiFetch<{ data: DmpStreetSearchItem[] }>(`/dmp/address-search?${params}`);
      setSResults(res.data);
    } catch (err) {
      setSError((err as Error)?.message || t('tickets:dmpLookup.error'));
      setSResults(null);
    } finally {
      setSLoading(false);
    }
  }, [t]);
  const searchStreet = () => {
    if (sStreet.trim().length < 3) return;
    clearJvidSearch();
    void searchStreetWith(sStreet, sNumber, sExt, sPostal);
  };

  // Apply what the caller asked for — same rules as the drawer's open() had: a jvid loads it, otherwise
  // a usable street pre-fills and runs the search, and anything else leaves the panel untouched.
  useEffect(() => {
    if (!request) return;
    if (request.jvid != null) { goTo(request.jvid); return; }
    const street = request.prefill?.street?.trim() ?? '';
    if (street.length >= 3) {
      const number = request.prefill?.number?.trim() ?? '';
      const ext = request.prefill?.ext?.trim() ?? '';
      const postal = request.prefill?.postal?.trim() ?? '';
      clearJvidSearch();
      setSStreet(street);
      setSNumber(number);
      setSExt(ext);
      setSPostal(postal);
      void searchStreetWith(street, number, ext, postal);
    }
    // Only the nonce drives this: re-running on the callbacks would reload on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.nonce]);

  const submit = () => {
    const n = Number(input.trim());
    if (!Number.isInteger(n) || n <= 0) return;
    clearStreetSearch();
    void load(n);
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder={t('tickets:dmpLookup.placeholder')}
          inputMode="numeric"
          aria-label={t('tickets:dmpLookup.placeholder')}
        />
        <Button onClick={submit} disabled={loading || !/^\d+$/.test(input.trim())}>
          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />}
          {t('tickets:dmpLookup.search')}
        </Button>
      </div>

      {error && <p className="mt-3 text-xs font-medium text-red-600" role="alert">{error}</p>}

      {/* Street search — "je n'ai pas le jvid" entry: street (min 3 chars) + optional number,
          extension (e.g. 5c — normalized match) and postal code (user ask 2026-07-31). */}
      <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {t('tickets:dmpLookup.streetSearchTitle')}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <Input
            value={sStreet}
            onChange={(e) => setSStreet(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void searchStreet(); }}
            placeholder={t('tickets:dmpLookup.streetPlaceholder')}
            aria-label={t('tickets:dmpLookup.streetPlaceholder')}
            className="min-w-[12rem] flex-1 bg-white"
          />
          <Input
            value={sNumber}
            onChange={(e) => setSNumber(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void searchStreet(); }}
            placeholder={t('tickets:dmpLookup.numberPlaceholder')}
            aria-label={t('tickets:dmpLookup.numberPlaceholder')}
            className="w-20 shrink-0 bg-white"
          />
          <Input
            value={sExt}
            onChange={(e) => setSExt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void searchStreet(); }}
            placeholder={t('tickets:dmpLookup.extPlaceholder')}
            aria-label={t('tickets:dmpLookup.extPlaceholder')}
            className="w-28 shrink-0 bg-white"
          />
          <Input
            value={sPostal}
            onChange={(e) => setSPostal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void searchStreet(); }}
            placeholder={t('tickets:dmpLookup.postalPlaceholder')}
            aria-label={t('tickets:dmpLookup.postalPlaceholder')}
            inputMode="numeric"
            className="w-28 shrink-0 bg-white"
          />
          <Button variant="outline" onClick={() => void searchStreet()} disabled={sLoading || sStreet.trim().length < 3}>
            {sLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
        {sError && <p className="mt-2 text-xs font-medium text-red-600" role="alert">{sError}</p>}
        {sResults && (
          sResults.length === 0 ? (
            <p className="mt-2 text-xs text-slate-400">{t('tickets:dmpLookup.streetNoResult')}</p>
          ) : (
            <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white">
              <table className="w-full text-xs">
                <tbody>
                  {sResults.map((r) => (
                    <tr
                      key={r.jvid}
                      onClick={() => goTo(r.jvid)}
                      className="cursor-pointer border-t border-slate-100 first:border-t-0 hover:bg-brand-rose/[0.04]"
                    >
                      <td className="px-2.5 py-1.5 font-medium text-slate-700">{r.jvid}</td>
                      <td className="px-2.5 py-1.5 text-slate-600">
                        {addressLine(r.streetName, r.houseNumber, r.houseNumberExtension)}
                        {r.busNumber?.trim() ? ` / ${r.busNumber}` : ''}
                      </td>
                      <td className="px-2.5 py-1.5 text-slate-500">{[r.postalCode, r.city ?? r.municipality].filter(Boolean).join(' ')}</td>
                      <td className="px-2.5 py-1.5 text-slate-500">{r.status ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sResults.length >= 50 && (
                <p className="border-t border-slate-100 px-2.5 py-1.5 text-[10px] text-slate-400">
                  {t('tickets:dmpLookup.streetTruncated')}
                </p>
              )}
            </div>
          )
        )}
      </div>

      {data && (
        <div className="mt-4 space-y-4">
          <VerdictPills data={data} onOpen={setOpenKey} />
          <AddressActionsMenu
            data={data}
            ticket={ticket}
            linkBusy={linkBusy}
            onLink={onLink}
            onCreateAddress={onCreateAddress}
            onAddBox={onAddBox}
            onEditAddress={onEditAddress}
            onSwapBusNumber={onSwapBusNumber}
            onNeighbours={showNeighbours ? () => setNeighboursOpen(true) : undefined}
          />
          {showNeighbours && neighboursOpen && (
            <NeighboursSection
              jvid={data.reference.jvid}
              archived={data.state === 'archived'}
              onOpen={goTo}
              onClose={() => setNeighboursOpen(false)}
            />
          )}
          <div>
            <AttentionDetails ruleIds={computeAttentionPoints(data.attributes)} />
            <AddressReferenceView
              reference={data.reference}
              attributes={data.attributes}
              buildFiles={data.buildFiles}
              renderFix={renderFix}
            />
            {fixModal}
            {/* Corriger le statut SEUL — action d'exception, console uniquement. Elle vit sous le
                verdict et non dedans : ce n'est pas la réparation d'une condition, c'est une
                dérogation à la règle « le statut se déduit des actions ». */}
            <AddressStatusFix jvid={data.reference.jvid} attributes={data.attributes} onDone={() => void load(data.reference.jvid)} />
            {data.pstIndexReady && (
              <p className="mt-2 text-[11px] text-slate-500">
                {data.pstFile
                  ? t('tickets:dmpLookup.inPst', { file: data.pstFile.split('/').pop() })
                  : t('tickets:dmpLookup.notInPst')}
              </p>
            )}
          </div>

          {/* Les motifs laissés sur cette adresse (22-10) — rien quand il n'y en a pas. Même clé que le bloc de
              mapping : une correction de colonne enregistrée doit faire apparaître son motif sans recharger. */}
          <AddressCommentsBlock key={`c:${data.reference.jvid}:${loadedAt}`} jvid={data.reference.jvid} />

          {/* N39 — ce que chaque opérateur déclare pour cette adresse. La clé porte le chargement : après une
              écriture la page redemande le même JVID, et le bloc doit relire lui aussi. */}
          <ConsoleMappingBlock
            key={`${data.reference.jvid}:${loadedAt}`}
            jvid={data.reference.jvid}
            onEdit={onEditMapping ? (mapping, row) => onEditMapping(data, mapping, row) : undefined}
          />

          {/* The detail of a pill, on demand only (user ask 2026-08-11) — three tables pinned under
              every lookup buried the fiche; the verdict line answers, this answers "why". */}
          {openKey && openKey !== 'PST' && data.buildFiles?.some((h) => h.kind === openKey) && (
            <DetailModal title={t(`tickets:dmpLookup.buildFile.${openKey}`)} onClose={() => setOpenKey(null)}>
              <BuildFileCard hit={data.buildFiles.find((h) => h.kind === openKey)!} />
              {/* « Forcer » (17-09) — seulement dans la fiche de la pastille ROUGE, et seulement quand la page l'offre
                  (admins). La fiche se ferme d'abord : la fenêtre de la ligne à écrire est rendue par la page. */}
              {openKey === 'BLACKLIST' && onForceBlacklist && data.buildFiles.find((h) => h.kind === 'BLACKLIST')?.found && (
                <div className="border-t border-slate-100 px-4 py-3">
                  <Button
                    size="sm"
                    className="bg-red-600 text-white hover:bg-red-700"
                    onClick={() => { setOpenKey(null); onForceBlacklist(data); }}
                  >
                    <AlertTriangle className="mr-1.5 h-4 w-4" />
                    {t('tickets:dmpLookup.forceFeedback.button')}
                  </Button>
                  <p className="mt-1.5 text-[11px] text-slate-500">{t('tickets:dmpLookup.forceFeedback.buttonHint')}</p>
                </div>
              )}
            </DetailModal>
          )}
          {openKey === 'PST' && (
            <DetailModal title={t('tickets:dmpLookup.pstPill')} onClose={() => setOpenKey(null)}>
              <div className="p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                  {t('tickets:dmpLookup.pstPill')}
                </p>
                <p className="mt-2 text-sm text-slate-700">
                  {data.pstFile
                    ? t('tickets:dmpLookup.inPst', { file: data.pstFile.split('/').pop() })
                    : t('tickets:dmpLookup.notInPst')}
                </p>
                {data.pstFile && <p className="mt-1 break-all font-mono text-[11px] text-slate-400">{data.pstFile}</p>}
              </div>
            </DetailModal>
          )}
          {/* Convention gardée (décision user du 14-09) : la pastille est un BOUTON, le lien vit dans la
              modale — c'est LÀ que se lit qui est signé et qui manque, puisque la couleur ne le dit plus. */}
          {openKey === 'SSVTSA' && data.ssvTsa && (
            <DetailModal title={t('tickets:dmpLookup.ssvTsa.title')} onClose={() => setOpenKey(null)}>
              <SsvTsaCard docs={data.ssvTsa} />
            </DetailModal>
          )}

          {/* "Nothing hidden": the COMPLETE public.address row, every column incl. nulls. */}
          {data.raw && (
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                {/* Le compteur du titre dit le TOTAL, jamais le résultat filtré — sinon « 3 colonnes »
                    laisserait croire que l'adresse n'en porte que 3. */}
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('tickets:dmpLookup.rawTitle', { count: rawTotal })}
                </p>
                {/* Le champ doit se voir : au premier jet il était en h-7/11px/icône grise, plus petit
                    que tous les autres champs de la console, et il se fondait dans le titre (retour user
                    du 11-09 « on ne la voit pas assez »). Il reprend donc l'idiome des autres Input du
                    panneau — fond blanc, accent brand-rose — en légèrement plus marqué. */}
                <div className="relative w-full sm:w-72">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-rose" />
                  <Input
                    value={rawFilter}
                    onChange={(e) => setRawFilter(e.target.value)}
                    placeholder={t('tickets:dmpLookup.rawFilterPlaceholder')}
                    aria-label={t('tickets:dmpLookup.rawFilterPlaceholder')}
                    className="h-9 border-brand-rose/40 bg-white pl-9 text-xs shadow-sm focus-visible:ring-2 focus-visible:ring-brand-rose/40"
                  />
                </div>
              </div>
              {rawRows.length === 0 ? (
                <p className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-center text-[11px] text-slate-400">
                  {t('tickets:dmpLookup.rawNoMatch')}
                </p>
              ) : (
                <div className="max-h-96 overflow-y-auto rounded-xl border border-slate-200">
                  <table className="w-full text-[11px]">
                    <tbody>
                      {rawRows.map(([col, val]) => (
                        <tr key={col} className="group border-t border-slate-100 first:border-t-0">
                          <td className="whitespace-nowrap px-3 py-1 font-mono font-medium text-slate-600">{col}</td>
                          <td className={cn('break-all px-3 py-1 font-mono', val == null ? 'text-slate-300' : 'text-slate-800')}>
                            {val == null ? '∅' : typeof val === 'object' ? JSON.stringify(val) : String(val)}
                          </td>
                          {/* Le crayon (admins, 17-09) : sur TOUTES les colonnes — c'est l'éditeur qui dit, colonne par
                              colonne, ce qui s'écrit ici, ce qui passe par « Modifier l'adresse » et ce qui ne se
                              modifie jamais. Discret au repos, net au survol de la ligne. */}
                          {onEditColumn && (
                            <td className="w-8 px-1 py-1 text-right">
                              <button
                                type="button"
                                onClick={() => onEditColumn(data, col)}
                                title={t('tickets:dmpLookup.columnEdit.editBtn', { column: col })}
                                aria-label={t('tickets:dmpLookup.columnEdit.editBtn', { column: col })}
                                className="rounded p-1 text-slate-300 hover:bg-brand-rose/10 hover:text-brand-rose group-hover:text-slate-500"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="mt-1.5 text-[10px] text-slate-400">
                {rawQuery !== '' && (
                  <span className="font-medium text-slate-500">
                    {t('tickets:dmpLookup.rawFiltered', { count: rawRows.length, total: rawTotal })}
                    {' · '}
                  </span>
                )}
                {t('tickets:dmpLookup.rawHint')}
              </p>
            </div>
          )}

          {data.apartments && data.apartments.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
                {t('tickets:dmpLookup.building', { count: data.apartments.length })}
              </p>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-left uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-2 font-semibold">JVID</th>
                      <th className="px-3 py-2 font-semibold">{t('tickets:dmpLookup.colAddress')}</th>
                      <th className="px-3 py-2 font-semibold">{t('tickets:dmpLookup.colBus')}</th>
                      <th className="px-3 py-2 font-semibold">{t('tickets:dmpLookup.colStatus')}</th>
                      <th className="px-3 py-2 font-semibold">{t('tickets:dmpLookup.colDelivery')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.apartments.map((a) => (
                      <tr
                        key={a.jvid}
                        onClick={() => goTo(a.jvid)}
                        className={cn(
                          'cursor-pointer border-t border-slate-100 hover:bg-brand-rose/[0.04]',
                          a.isCurrent && 'bg-rose-50/60 font-semibold',
                        )}
                      >
                        <td className="px-3 py-1.5 text-slate-700">{a.jvid}</td>
                        <td className="px-3 py-1.5 text-slate-600">
                          {addressLine(a.streetName, a.houseNumber, a.houseNumberExtension)}
                        </td>
                        <td className="px-3 py-1.5 text-slate-600">{a.busNumber?.trim() || '—'}</td>
                        <td className="px-3 py-1.5 text-slate-600">{a.attributes?.status ?? '—'}</td>
                        <td className="px-3 py-1.5 text-slate-600">
                          {a.attributes?.deliveryStatus != null ? (
                            <span
                              className={cn(
                                'cursor-help underline decoration-dotted underline-offset-2',
                                isBlockedDeliveryCode(Number(a.attributes.deliveryStatus))
                                  ? 'font-semibold text-red-700 decoration-red-300'
                                  : 'decoration-slate-300',
                              )}
                              title={formatDeliveryStatus(Number(a.attributes.deliveryStatus))}
                            >
                              {a.attributes.deliveryStatus}
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-1.5 text-[10px] text-slate-400">{t('tickets:dmpLookup.rowHint')}</p>
            </div>
          )}
        </div>
      )}
    </>
  );
}
